// Plan 23a Task 5.3 — Room Code Display primitive.
// Behavior contract: docs/design/components.md §15 lines 541–562.
//
// NO gsap — plain CSS + setTimeout only.
// Tokens from docs/design/tokens.json:
//   typography.family.headline = 'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif
//   typography.size.h1         = 48px
//   typography.letter-spacing.room-code = 8px
//   typography.family.body     = 'Nunito Sans', 'Nunito', 'Arial Rounded MT Bold', sans-serif
//   typography.size.caption    = 14px
//   color.text.primary         = #FFFFFF
//   color.text.secondary       = #B0B8C8
//   color.text.success         = #5CAD40

import type { UIComponentHandle, UIComponentConfig } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export interface RoomCodeOpts {
  /** Initial room code, e.g. 'ABCD'. Displayed ALL-CAPS. */
  code: string;
  /** Subtitle below the code. Default: 'Share this code!' */
  subtitle?: string;
}

export interface RoomCodeDisplayHandle extends UIComponentHandle {
  /** Update the displayed code and the value written to clipboard on click. */
  setCode(code: string): void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Icon characters
// ─────────────────────────────────────────────────────────────────────────────

/** Link/chain icon shown in the default (non-copied) state. */
const ICON_CHAIN = '🔗';
/** Checkmark icon shown in the copied state for 2 seconds. */
const ICON_CHECK = '✓';

/** Duration (ms) the copied checkmark icon is shown. */
const COPIED_DURATION_MS = 2000;

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createRoomCodeDisplay(
  config: UIComponentConfig & RoomCodeOpts,
): RoomCodeDisplayHandle {
  const { mount, code: initialCode, subtitle = 'Share this code!' } = config;

  let currentCode = initialCode;
  let disposed = false;
  let pendingTimeout: ReturnType<typeof setTimeout> | null = null;

  // ── Build DOM ──────────────────────────────────────────────────────────────

  // Outer container — centered column
  const el = document.createElement('div');
  el.className = 'ui-room-code';
  el.setAttribute('role', 'button');
  el.setAttribute('tabindex', '0');
  el.setAttribute('aria-label', `Copy room code ${currentCode.toUpperCase()}`);
  el.style.cssText = [
    'display: flex',
    'flex-direction: column',
    'align-items: center',
    'gap: 4px',
    'text-align: center',
    'cursor: pointer',
  ].join('; ');

  // Icon element — chain icon, 24px, color.text.secondary
  const iconEl = document.createElement('span');
  iconEl.setAttribute('data-icon', '');
  iconEl.textContent = ICON_CHAIN;
  iconEl.style.cssText = [
    'font-size: 24px',
    // color.text.secondary = #B0B8C8
    'color: var(--color-text-secondary, #B0B8C8)',
    'line-height: 1',
    'user-select: none',
    'transition: color 0.15s ease',
  ].join('; ');

  // Code text — h1 size, headline font, room-code letter-spacing, ALL-CAPS
  const codeEl = document.createElement('div');
  codeEl.setAttribute('data-code-text', '');
  codeEl.textContent = currentCode.toUpperCase();
  codeEl.style.cssText = [
    // typography.family.headline
    "font-family: var(--typography-family-headline, 'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif)",
    // typography.size.h1 = 48px
    'font-size: var(--typography-size-h1, 48px)',
    // typography.weight.bold
    'font-weight: 700',
    // color.text.primary = #FFFFFF
    'color: var(--color-text-primary, #FFFFFF)',
    // typography.letter-spacing.room-code = 8px
    'letter-spacing: var(--typography-letter-spacing-room-code, 8px)',
    'text-transform: uppercase',
    'line-height: 1.1',
    'user-select: none',
  ].join('; ');

  // Subtitle text — caption size, body font, color.text.secondary
  const subtitleEl = document.createElement('div');
  subtitleEl.className = 'room-code-subtitle';
  subtitleEl.textContent = subtitle;
  subtitleEl.style.cssText = [
    // typography.family.body
    "font-family: var(--typography-family-body, 'Nunito Sans', 'Nunito', 'Arial Rounded MT Bold', sans-serif)",
    // typography.size.caption = 14px
    'font-size: var(--typography-size-caption, 14px)',
    // color.text.secondary = #B0B8C8
    'color: var(--color-text-secondary, #B0B8C8)',
    'line-height: 1.4',
    'user-select: none',
  ].join('; ');

  el.appendChild(iconEl);
  el.appendChild(codeEl);
  el.appendChild(subtitleEl);

  // ── Helpers ────────────────────────────────────────────────────────────────

  function clearCopied(): void {
    pendingTimeout = null;
    // Restore chain icon + secondary color
    iconEl.textContent = ICON_CHAIN;
    iconEl.style.color = 'var(--color-text-secondary, #B0B8C8)';
    // Remove copied marker from icon
    iconEl.removeAttribute('data-copied');
    el.removeAttribute('data-copied');
    el.removeAttribute('data-copy-status');
    subtitleEl.textContent = subtitle;
  }

  function showCopiedFeedback(): void {
    iconEl.textContent = ICON_CHECK;
    iconEl.style.color = 'var(--color-text-success, #5CAD40)';
    iconEl.setAttribute('data-copied', '');
    el.setAttribute('data-copied', '');
    el.setAttribute('data-copy-status', 'copied');
    subtitleEl.textContent = 'Copied!';
  }

  function showCopyFailedFeedback(): void {
    iconEl.textContent = '!';
    iconEl.style.color = 'var(--color-text-danger, #E04848)';
    el.setAttribute('data-copy-status', 'failed');
    subtitleEl.textContent = 'Copy failed';
  }

  function scheduleFeedbackClear(): void {
    if (pendingTimeout !== null) {
      clearTimeout(pendingTimeout);
      pendingTimeout = null;
    }
    pendingTimeout = setTimeout(clearCopied, COPIED_DURATION_MS);
  }

  async function writeRoomCodeToClipboard(): Promise<boolean> {
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(currentCode);
        return true;
      } catch {
        // Fall through to legacy copy below.
      }
    }
    try {
      const ta = document.createElement('textarea');
      ta.value = currentCode;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      ta.style.pointerEvents = 'none';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const copied = document.execCommand('copy');
      document.body.removeChild(ta);
      return copied;
    } catch {
      return false;
    }
  }

  function handleCodeClick(event?: Event): void {
    if (disposed) return;
    event?.preventDefault();

    showCopiedFeedback();
    scheduleFeedbackClear();
    void writeRoomCodeToClipboard().then((copied) => {
      if (disposed || copied) return;
      showCopyFailedFeedback();
      scheduleFeedbackClear();
    });
  }

  function handleKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    handleCodeClick(event);
  }

  el.addEventListener('click', handleCodeClick);
  el.addEventListener('keydown', handleKeydown);

  // ── setCode ────────────────────────────────────────────────────────────────

  function setCode(code: string): void {
    if (disposed) return;
    currentCode = code;
    codeEl.textContent = code.toUpperCase();
    el.setAttribute('aria-label', `Copy room code ${code.toUpperCase()}`);
  }

  // ── dispose ────────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Clear pending timeout to prevent post-dispose state mutations
    if (pendingTimeout !== null) {
      clearTimeout(pendingTimeout);
      pendingTimeout = null;
    }

    // Remove click listener
    el.removeEventListener('click', handleCodeClick);
    el.removeEventListener('keydown', handleKeydown);

    // Detach from DOM if mounted
    if (el.parentNode) {
      el.parentNode.removeChild(el);
    }
  }

  // ── Optional mount at construction time ───────────────────────────────────

  if (mount && !mount.contains(el)) {
    mount.appendChild(el);
  }

  return {
    element: el,
    setCode,
    dispose,
  };
}
